
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { WAREHOUSE_REJECTION_REASONS } from '@/types/database.types';

export interface InspectionData {
  receivedQuantityKg: number;
  inspectionNotes: string;
  rejectionReason: string;
}

interface InspectionFormCardProps {
  inspectionData: InspectionData;
  setInspectionData: React.Dispatch<React.SetStateAction<InspectionData>>;
  onConfirm: () => void;
  onReject: () => void;
  isSubmitting: boolean;
  expectedQuantity: number;
  compensationKg: number;
}

const InspectionFormCard: React.FC<InspectionFormCardProps> = ({
  inspectionData,
  setInspectionData,
  onConfirm,
  onReject,
  isSubmitting,
  expectedQuantity,
  compensationKg
}) => {
  // Calculate net payable from received quantity minus compensation (floor at 0)
  const netPayableKg = Math.max(0, (inspectionData.receivedQuantityKg || 0) - compensationKg);
  const hasVariance = Math.abs(expectedQuantity - (inspectionData.receivedQuantityKg || 0)) > 0.5;
  const receivedDiff = (inspectionData.receivedQuantityKg || 0) - expectedQuantity;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Warehouse Receiving Form</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Show quality compensation from quality inspection - read only */}
        {compensationKg > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
            <p className="font-medium">Quality Compensation: {compensationKg.toFixed(1)} KG</p>
            <p className="text-xs mt-1">From quality inspection - this will be deducted from net payable</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="expectedQuantity">Expected Quantity (KG)</Label>
            <div className="h-10 px-3 flex items-center bg-muted rounded-md text-sm font-medium">
              {expectedQuantity.toFixed(1)} KG
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="receivedQuantityKg">Received Quantity (KG)</Label>
            <Input
              id="receivedQuantityKg"
              type="number"
              min="0"
              step="0.1"
              value={inspectionData.receivedQuantityKg || ''}
              onChange={(e) =>
                setInspectionData(prev => ({ ...prev, receivedQuantityKg: parseFloat(e.target.value) || 0 }))
              }
              placeholder={`${expectedQuantity}`}
            />
            {hasVariance && (
              <p className={`text-xs ${receivedDiff < 0 ? 'text-destructive' : 'text-green-600'}`}>
                {receivedDiff > 0 ? '+' : ''}{receivedDiff.toFixed(1)} KG vs expected
              </p>
            )}
          </div>
        </div>

        {/* Net Payable - calculated automatically */}
        <div className="space-y-2">
          <Label>Net Payable (calculated)</Label>
          <div className="h-10 px-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-md text-sm font-medium text-green-700">
            <span>{netPayableKg.toFixed(1)} KG</span>
            <span className="text-xs text-green-600">
              = Received ({(inspectionData.receivedQuantityKg || 0).toFixed(1)}) - Compensation ({compensationKg.toFixed(1)})
            </span>
          </div>
        </div>

        {hasVariance && (
          <div className="flex items-center gap-2 text-amber-600 text-sm p-2 bg-amber-50 rounded-md">
            <AlertTriangle className="h-4 w-4" />
            <span>
              Variance of {Math.abs(receivedDiff).toFixed(1)} KG between expected and received
            </span>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="inspectionNotes">Inspection Notes</Label>
          <Textarea
            id="inspectionNotes"
            value={inspectionData.inspectionNotes}
            onChange={(e) =>
              setInspectionData(prev => ({ ...prev, inspectionNotes: e.target.value }))
            }
            placeholder="Add any inspection observations..."
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
          <Select
            value={inspectionData.rejectionReason}
            onValueChange={(value) =>
              setInspectionData(prev => ({ ...prev, rejectionReason: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select rejection reason..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(WAREHOUSE_REJECTION_REASONS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            onClick={onConfirm}
            disabled={isSubmitting || !inspectionData.receivedQuantityKg}
            className="flex-1"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Confirm
          </Button>
          <Button
            variant="destructive"
            onClick={onReject}
            disabled={isSubmitting || !inspectionData.rejectionReason}
            className="flex-1"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InspectionFormCard;
